/**
 * Shared v2 components — Nav, Footer, CTAs, reveal preset.
 * Both /v2/style and /v2/home consume these so the aesthetic stays consistent
 * as we build more pages.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Menu, X } from 'lucide-react';
import { V2_COLORS } from './tokens';

// Standard scroll-reveal preset used across the site.
export const V2_REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export function V2Nav({ scheme = 'dark', active = 'home' }) {
  const isDark = scheme === 'dark';
  const bg = isDark ? 'bg-[#07070C]/70' : 'bg-[#F5F1E8]/85';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const label = isDark ? 'text-white' : 'text-[#1A1A22]';
  const muted = isDark ? 'text-[#B8B4A6]' : 'text-[#9C978A]';
  const cta = isDark
    ? 'ring-white/20 hover:ring-white/50 hover:bg-white hover:text-[#07070C]'
    : 'ring-black/20 hover:ring-black/60 hover:bg-[#07070C] hover:text-[#F5F1E8]';

  const items = [
    { label: 'Work',       to: '/work',      key: 'work' },
    { label: 'Services',   to: '/services',  key: 'services' },
    { label: 'About',      to: '/about',     key: 'about' },
    { label: 'Insights',   to: '/insights',key: 'insights' },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl ${bg} border-b ${border}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="v2-nav-logo" aria-label="BrightLabs — Home" onClick={() => setMobileOpen(false)}>
            <img
              src="/brand/monogram.webp"
              alt=""
              width="51"
              height="40"
              className="h-9 md:h-10 w-auto select-none"
              draggable={false}
              fetchpriority="high"
            />
            <span
              className="hidden sm:inline text-[18px] md:text-[20px] font-bold tracking-tight"
              style={{
                letterSpacing: '-0.02em',
                color: isDark ? '#F5F1E8' : '#1A1A22',
              }}
            >
              Bright<span style={{ background: 'linear-gradient(90deg,#3B4DFF,#C939FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Labs</span>
            </span>
          </Link>
          <div className={`hidden md:flex items-center gap-8 v2-mono text-[12px] uppercase tracking-[0.22em] ${muted}`}>
            {items.map((it) => (
              <a
                key={it.key}
                href={it.to}
                data-testid={`v2-nav-${it.key}`}
                className={`${active === it.key ? label : ''} hover:${label.replace('text-', 'text-')} transition-colors`}
              >
                {it.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/start"
              data-testid="v2-nav-cta"
              className={`hidden sm:inline-flex v2-mono text-[11px] tracking-[0.24em] uppercase px-4 py-2 rounded-full ring-1 ${cta} ${label} transition-all`}
            >
              Start a Project <ArrowUpRight size={11} className="inline ml-1 -mt-0.5" />
            </Link>
            <button
              type="button"
              className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full ring-1 ${isDark ? 'ring-white/20 text-white' : 'ring-black/20 text-[#1A1A22]'}`}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="v2-nav-mobile-toggle"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer — MUST be a sibling of <nav>, not a child.
          The nav has `backdrop-blur-xl`, which per the CSS spec creates a
          containing block for position:fixed descendants — that would clip
          the drawer to the nav's height (~64px) instead of covering the
          viewport. Rendering it here keeps `top:64px; bottom:0` relative to
          the actual viewport. */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-x-0 top-[64px] bottom-0 z-50 bg-[#07070C] flex flex-col p-6 pt-10 gap-2 v2-mono uppercase tracking-[0.22em] overflow-y-auto"
          data-testid="v2-nav-mobile-drawer"
        >
          {items.map((it) => (
            <Link
              key={it.key}
              to={it.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`v2-nav-mobile-${it.key}`}
              className={`py-4 border-b border-white/[0.08] text-[26px] font-bold tracking-tight normal-case ${active === it.key ? 'text-[#E85D2F]' : 'text-white'}`}
              style={{ fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic' }}
            >
              {it.label}
            </Link>
          ))}
          <Link
            to="/start"
            onClick={() => setMobileOpen(false)}
            className="mt-8 inline-flex items-center justify-between px-6 py-4 rounded-full bg-[#E85D2F] text-[#07070C] font-bold text-[13px] tracking-[0.24em]"
            data-testid="v2-nav-mobile-cta"
          >
            Start a Project <ArrowUpRight size={16} />
          </Link>
          <div className="mt-10 pt-6 border-t border-white/[0.08] space-y-3 normal-case tracking-normal">
            <a href="tel:+441603976281" className="block text-[#B8B4A6] hover:text-white transition-colors text-[15px]" data-testid="v2-nav-mobile-phone">
              <span className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] mr-2">Call</span>
              +44 1603 976281
            </a>
            <a href="mailto:hello@brightlabs.live" className="block text-[#B8B4A6] hover:text-white transition-colors text-[15px]" data-testid="v2-nav-mobile-email">
              <span className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] mr-2">Email</span>
              hello@brightlabs.live
            </a>
          </div>
        </div>
      )}
    </>
  );
}

export function V2Footer() {
  const cols = [
    { label: 'Work', items: [
      { l: 'Selected Work', to: '/work' },
      { l: 'SugaSpice',     to: '/work/sugaspice' },
      { l: 'Cobra Thai',    to: '/work/cobra-thai' },
      { l: 'Brightridge',   to: '/work/brightridge' },
      { l: 'VeroBill',      to: '/work/verobill' },
    ]},
    { label: 'Services', items: [
      { l: 'Websites & Digital',    to: '/services/websites' },
      { l: 'Creative & Social',     to: '/services/creative-social' },
      { l: 'Paid Growth',           to: '/services/paid-growth' },
      { l: 'SEO',                   to: '/services/seo' },
      { l: 'Software & Automation', to: '/services/software' },
      { l: 'Custom CRM Development', to: '/services/custom-crm-development' },
      { l: 'Growth Partnership',    to: '/services/growth-partnership' },
    ]},
    { label: 'Guides', items: [
      { l: 'Website Cost UK',              to: '/insights/business-website-cost-uk' },
      { l: 'Redesign vs Rebuild',          to: '/insights/website-redesign-vs-rebuild' },
      { l: 'SEO vs Google Ads',            to: '/insights/seo-vs-google-ads' },
      { l: 'Agency vs Growth Partner',     to: '/insights/marketing-agency-vs-growth-partner' },
      { l: 'See all guides \u2192',         to: '/insights' },
    ]},
    { label: 'Locations & Industries', items: [
      { l: 'Norwich',              to: '/norwich' },
      { l: 'London',               to: '/london/digital-agency' },
      { l: 'B2B (UK)',              to: '/b2b-digital-marketing-agency' },
      { l: 'SaaS & Tech',           to: '/industries/saas-b2b-tech' },
      { l: 'Recruitment',           to: '/industries/recruitment-agencies' },
      { l: 'Private Clinics',       to: '/industries/private-clinics' },
    ]},
    { label: 'Company', items: [
      { l: 'About',    to: '/about' },
      { l: 'Insights', to: '/insights' },
      { l: 'Contact',  to: '/start' },
    ]},
  ];
  return (
    <footer className="border-t border-white/[0.08] pt-24 pb-14 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-footer">
      <div className="grid md:grid-cols-[1.3fr_1fr_1fr_0.9fr_1.1fr_0.8fr] gap-10 md:gap-8 mb-20">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <img src="/brand/monogram.webp" alt="" width="51" height="40" className="h-10 w-auto select-none" draggable={false} />
            <span className="text-[28px] font-bold tracking-tight" style={{ color: '#F5F1E8', letterSpacing: '-0.02em' }}>Bright<span style={{ background: 'linear-gradient(90deg,#3B4DFF,#C939FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Labs</span></span>
          </div>
          <div className="v2-h1 text-[42px] md:text-[52px] leading-[0.95] max-w-[10ch]">
            Let&rsquo;s make{' '}
            <em style={{ fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic' }}>something</em>{' '}
            good.
          </div>
          <Link to="/start" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full !bg-white !text-[#07070C] font-bold v2-mono text-[11px] tracking-[0.24em] uppercase transition hover:!bg-[#F5F1E8] active:scale-[0.98]" data-testid="v2-footer-cta">
            Start a Project <ArrowRight size={13} />
          </Link>
          <div className="mt-10 space-y-2 v2-mono text-[12px] tracking-[0.14em]">
            <a href="tel:+441603976281" data-testid="v2-footer-phone" className="block text-[#B8B4A6] hover:text-white transition-colors">
              <span className="text-[#9C978A] uppercase tracking-[0.24em] text-[10.5px] mr-2">Call</span>
              +44 1603 976281
            </a>
            <a href="mailto:hello@brightlabs.live" data-testid="v2-footer-email" className="block text-[#B8B4A6] hover:text-white transition-colors">
              <span className="text-[#9C978A] uppercase tracking-[0.24em] text-[10.5px] mr-2">Email</span>
              hello@brightlabs.live
            </a>
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.label}>
            <div className="v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A] mb-4">{c.label}</div>
            <ul className="space-y-2.5 text-[15px] text-[#B8B4A6]">
              {c.items.map((it) => (
                <li key={it.l}>
                  <Link to={it.to} data-testid={`v2-footer-link-${it.l.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} className="hover:text-white transition-colors">{it.l}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="pt-8 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-6 v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A]">
        <div>© BrightLabs 2026 · Norwich · London · Pune</div>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="v2-footer-privacy">Privacy</Link>
          <Link to="/terms"   className="hover:text-white transition-colors" data-testid="v2-footer-terms">Terms</Link>
          <Link to="/insights" className="hover:text-white transition-colors" data-testid="v2-footer-sitemap-link">All Insights</Link>
        </div>
      </div>
    </footer>
  );
}

export function PrimaryCTA({ children, to = '/start', className = '' }) {
  return (
    <Link
      to={to}
      className={`v2-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 px-6 py-3.5 rounded-full !bg-white !text-[#07070C] font-bold transition-all hover:!bg-[#F5F1E8] active:scale-[0.98] ${className}`}
      data-testid="v2-cta-primary"
    >
      {children} <ArrowRight size={13} />
    </Link>
  );
}

export function GhostCTA({ children, to = '#work', className = '', dark = true }) {
  const ring = dark ? 'ring-white/25 text-white hover:ring-white/60' : 'ring-black/25 text-[#1A1A22] hover:ring-black/60';
  return (
    <a
      href={to}
      className={`v2-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 px-6 py-3.5 rounded-full ring-1 ${ring} transition-all active:scale-[0.98] ${className}`}
      data-testid="v2-cta-ghost"
    >
      {children} <ArrowRight size={13} />
    </a>
  );
}

// Section kicker used on every editorial section header
export function V2Kicker({ num, children, warm = false }) {
  return (
    <span className={`v2-kicker ${warm ? '' : 'text-[#B8B4A6]'}`} style={warm ? { color: V2_COLORS.warm } : undefined}>
      <span className="opacity-70">{num} —</span> {children}
    </span>
  );
}
